import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { authorityGate, authorityGateAll, gateOpen } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { LoadFailure } from '../LoadFailure.js';
import { escalation, type Escalation, type EscalationCandidate } from './escalationClient.js';
import {
  decidableCandidates,
  dispositionSentence,
  escalationSentence,
  personLabel,
  redirectionSentence,
  relationshipSentence,
  ruleSentence,
} from './escalationLogic.js';

/**
 * ===========================================================================
 * ESCALATE THIS FINDING — and say which of the three answers happened.
 * ===========================================================================
 *
 * The ladder was engine-complete and customer-invisible for months: eight
 * routes, a resolver that refuses rather than guesses, a CHECK constraint
 * behind it, and — measured by lane OPS-A across `packages/` and
 * `apps/admin/src/` — zero bindings and zero screens. Meanwhile the operations
 * team escalated by hand every evening at 20:00 from a spreadsheet.
 *
 * ---------------------------------------------------------------------------
 * WHY THERE IS A PREVIEW ABOVE THE BUTTON
 * ---------------------------------------------------------------------------
 * Because the answer is the product. A person about to escalate needs to know
 * whether this will reach somebody, and if not, WHICH kind of nothing it will
 * reach — an absent configuration, a named person who cannot act, or two
 * people nobody has chosen between. `previewEscalation` writes nothing
 * precisely so this can be asked without filling the management queue with
 * "No escalation owner configured" every time somebody opens a finding.
 *
 * The preview and the recorded escalation render through the SAME sentence
 * function. If they used different words, a reader would have to work out
 * whether the answer changed or only the wording did.
 *
 * ---------------------------------------------------------------------------
 * WHY THE BUTTON IS STILL PRESSED WHEN THE PREVIEW SAYS "NOBODY"
 * ---------------------------------------------------------------------------
 * It is not a dead control and it is not a mistake. The server answers 201 for
 * every outcome, deliberately: an escalation that resolved nobody still
 * HAPPENED, and recording it is what puts it in front of management. Disabling
 * the button on a non-routed preview would mean the only findings anybody
 * could escalate are the ones that were already going to be fine — and the
 * unconfigured ones, the ones that most need somebody's attention, would leave
 * no trace at all.
 *
 * The wording changes instead. "Escalate" becomes "Escalate anyway and put
 * this in front of management", which is what the act actually is.
 *
 * ---------------------------------------------------------------------------
 * AND WHY THE AMBIGUITY OFFERS ONLY THE CANDIDATES
 * ---------------------------------------------------------------------------
 * `decide` refuses a target the ladder never resolved — 409, not 400, because
 * the request is well formed and the caller is authorized; what is wrong is
 * that a decision may only pick among the targets organizational authority
 * produced. A directory picker here would be the invention the whole ruling
 * forbids, arriving through the door built to close it. So the control is a
 * list of the recorded eligible candidates and nothing else.
 */

const INSIGHTS_TRIAGE_KEY = 'insights:triage';
const DIRECTORY_READ_KEY = 'directory:read';

/*
 * THERE IS NO DIRECTORY INDEX ON THIS SCREEN ANY MORE, and its removal is the
 * substance of the change rather than housekeeping.
 *
 * This panel used to fetch `directory.workers()` and resolve every escalation
 * user id against it, because the escalation view carried bare ids. That is a
 * SECOND answer to who this reader may be told about, computed on the client
 * from a different query with a different scope — and a second answer is one
 * edit away from disagreeing with the server's. It also could not distinguish
 * a person withheld from this reader from a person who has left, which is why
 * `personLabel` had to say both.
 *
 * The server now sends `EscalationPerson` with the same `shown | withheld |
 * absent` discriminant a finding entity carries, resolved by the one
 * implementation that also answers it for a finding's owner. So the name a
 * reader sees is the name the SERVER decided they may have, and there is
 * nothing left here to disagree with it.
 */

/** One resolved answer, rendered the same whether it is a preview or a record. */
function Answer({
  answer,
  what,
}: {
  readonly answer: {
    outcome: Escalation['outcome'];
    candidateCount: number;
    authorizedCandidateCount: number;
    resolutionRule: Escalation['resolutionRule'];
    resolvedVia: Escalation['resolvedVia'];
    target: Escalation['target'];
    relationship: Escalation['relationship'];
    coverage: Escalation['coverage'];
    candidates: readonly EscalationCandidate[];
    priorOwner: Escalation['priorOwner'];
    orgLocalDate: string;
    escalatedAt: string;
    policyKind: Escalation['policyKind'];
    policyKey: string | null;
    agentRunId: string | null;
  };
  readonly what: 'preview' | 'record';
}) {
  const sentence = escalationSentence(answer);
  const redirection = redirectionSentence(answer);
  const relationship = relationshipSentence(answer.relationship);
  const target = personLabel(answer.target);
  const priorOwner = personLabel(answer.priorOwner);

  return (
    <div
      className="rn-col"
      data-escalation-answer={what}
      data-escalation-outcome={sentence.outcome}
      data-escalation-reason={sentence.reason}
      data-escalation-tone={sentence.tone}
    >
      <p>
        <span className="rn-strong">{sentence.headline}</span>{' '}
        <span className="rn-muted">{sentence.detail}</span>
      </p>
      <p className="rn-muted" data-escalation-remedy>
        {sentence.remedy}
      </p>

      {answer.outcome === 'routed' ? (
        <dl className="rn-facts" data-escalation-routed>
          <dt>Reached</dt>
          {/* `data-escalation-target` names WHICH of the four this is, not
              merely named/unnamed: "withheld" and "absent" send a reader to two
              different remedies and a boolean would collapse them again. */}
          <dd data-escalation-target={target.disclosure}>{target.label}</dd>
          <dt>Escalated past</dt>
          <dd data-escalation-prior-owner={priorOwner.disclosure}>
            {answer.priorOwner === null
              ? 'Nobody — this finding had no owner to move it from.'
              : priorOwner.label}
          </dd>
          <dt>Rule that selected them</dt>
          <dd>{ruleSentence(answer.resolutionRule)}</dd>
          {relationship === null ? null : (
            <>
              <dt>Relationship used, and its version</dt>
              <dd>{relationship}</dd>
            </>
          )}
          {redirection === null ? null : (
            <>
              <dt>Redirected by coverage</dt>
              <dd data-escalation-redirected>{redirection}</dd>
            </>
          )}
          <dt>Policy</dt>
          <dd>
            {answer.policyKind === null
              ? 'No policy or playbook selected this; it came from the org chart.'
              : `${answer.policyKind} · ${answer.policyKey ?? '—'}`}
          </dd>
          <dt>Agent run</dt>
          <dd>
            {answer.agentRunId === null ? (
              'No agent run — a person did this.'
            ) : (
              <code>{answer.agentRunId}</code>
            )}
          </dd>
          <dt>When</dt>
          <dd>
            <time dateTime={answer.escalatedAt}>
              {new Date(answer.escalatedAt).toLocaleString()}
            </time>{' '}
            <span className="rn-muted">
              · read against your organization’s calendar day {answer.orgLocalDate}
            </span>
          </dd>
        </dl>
      ) : null}

      {/* THE PEOPLE THE LADDER NAMED — shown for every non-routed answer,
          because "who did it find and why were they not it" is the question a
          reader has next, and for `unconfigured` the honest answer is nobody. */}
      {answer.outcome === 'routed' ? null : (
        <div data-escalation-candidates>
          <h4 className="rn-card-title">Who your records named</h4>
          {answer.candidates.length === 0 ? (
            <p className="rn-muted" data-escalation-candidates-empty>
              Nobody. Every rung of the ladder was silent — this is what “no escalation owner
              configured” means, and it is not a list you are being kept from seeing.
            </p>
          ) : (
            <ul className="rn-list">
              {answer.candidates.map((candidate) => {
                const person = personLabel(candidate.delivered);
                return (
                  <li
                    key={`${candidate.ordinal}:${candidate.candidateUserId}`}
                    data-escalation-candidate={candidate.disposition}
                    data-escalation-candidate-person={person.disclosure}
                  >
                    <span className="rn-strong">{person.label}</span>{' '}
                    <span className="rn-muted">{dispositionSentence(candidate.disposition)}</span>
                    <br />
                    <span className="rn-muted">{ruleSentence(candidate.rule)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function EscalationPanel({ findingId }: { readonly findingId: string }) {
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  /**
   * THE ESCALATE DOOR CHARGES TWO KEYS, so it is gated with `authorityGateAll`
   * rather than with two booleans ANDed together.
   *
   * `POST .../escalate` checks `insights:triage` AND `directory:read` — an
   * escalation names a person, and naming one is reading the workforce. A
   * screen that gated on triage alone would offer a control that always
   * refuses; a screen that wrote its own "you also need directory access"
   * sentence off `!mayReadDirectory` would be picking a sentence off a boolean
   * that also means "the set has not arrived" and "the set could not be read"
   * — the defect `unknown-is-not-denied.test.ts` sweeps for, and it caught
   * exactly that here on the first run of this file.
   */
  const escalateGate = authorityGateAll(permissions, [INSIGHTS_TRIAGE_KEY, DIRECTORY_READ_KEY]);
  const mayEscalate = gateOpen(escalateGate);
  /** The history charges the directory key alone: reading is reading. */
  const directoryGate = authorityGate(permissions, DIRECTORY_READ_KEY);
  const mayReadDirectory = gateOpen(directoryGate);
  const [chosen, setChosen] = useState<Record<string, string>>({});

  // The preview charges the TRIAGE key on the server, so it is not run for a
  // reader who may only read. A default-deny `enabled:` is the safe direction:
  // it asks nothing while the permission answer is still unknown.
  const preview = useQuery({
    queryKey: ['insights', 'finding', findingId, 'escalation-preview'],
    queryFn: () => escalation.preview(findingId),
    enabled: mayEscalate && mayReadDirectory,
  });

  const history = useQuery({
    queryKey: ['insights', 'finding', findingId, 'escalations'],
    queryFn: () => escalation.forFinding(findingId),
    enabled: mayReadDirectory,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({
      queryKey: ['insights', 'finding', findingId, 'escalations'],
    });
    void queryClient.invalidateQueries({
      queryKey: ['insights', 'finding', findingId, 'escalation-preview'],
    });
    void queryClient.invalidateQueries({ queryKey: ['insights', 'escalations', 'open'] });
  };

  const escalateNow = useMutation({
    mutationFn: () => escalation.escalate(findingId),
    onSuccess: invalidate,
  });

  const decide = useMutation({
    mutationFn: (input: { escalationId: string; targetUserId: string }) =>
      escalation.decide(input.escalationId, input.targetUserId),
    onSuccess: invalidate,
  });

  const previewAnswer = preview.data;
  const escalations = history.data ?? [];

  return (
    <section className="rn-card" data-escalation-panel="finding">
      <h2 className="rn-card-title">Escalation</h2>
      <p className="rn-muted">
        Escalating resolves an owner from your organization’s own records — a configured target for
        this policy, the assigned owner, the manager over this finding’s program or site, then your
        fallback role — and stops at the first of those that names anybody. Phibian never picks
        between two equally valid people; it says so and waits for you.
      </p>

      {/* ---- What would happen, before anything happens ------------------ */}
      <GateNotice
        gate={escalateGate}
        what="escalate this finding"
        heading="Escalating this is not yours to do"
        detail="Escalating a finding records a durable decision with your name on it, and it names a person — so it needs the Insights triage permission and directory access. You can still see where this finding has been escalated to before."
        onRetry={() => permissions.refetch()}
      />

      {mayEscalate ? (
        <div data-escalation-preview-block>
          <h3 className="rn-card-title">Who this would reach</h3>
          {preview.isLoading ? (
            <p className="rn-muted" role="status">
              Working out who this would escalate to…
            </p>
          ) : preview.isError ? (
            <LoadFailure
              error={preview.error}
              message="We could not work out who this would escalate to. Nothing has been escalated."
              onRetry={() => void preview.refetch()}
              refusedHeading="You cannot see who this would escalate to"
            />
          ) : previewAnswer ? (
            <>
              <Answer answer={previewAnswer} what="preview" />
              <p className="rn-muted">
                Nothing has been recorded yet. Asking this question writes no row, so opening a
                finding never fills the escalation queue.
              </p>
              <Button
                onClick={() => escalateNow.mutate()}
                disabled={escalateNow.isPending}
                variant="secondary"
              >
                {previewAnswer.outcome === 'routed'
                  ? 'Escalate'
                  : 'Escalate anyway and put this in front of management'}
              </Button>
              {escalateNow.isError ? (
                <LoadFailure
                  error={escalateNow.error}
                  message="We could not record that escalation. Nothing has changed."
                  refusedHeading="You cannot escalate this finding"
                />
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {/* ---- Where it has been ------------------------------------------- */}
      <div data-escalation-history>
        <h3 className="rn-card-title">Escalations recorded</h3>
        {/*
          THE REFUSAL IS RENDERED BY THE COMPONENT THAT KNOWS ALL FOUR ANSWERS.
          `refusedNode` keeps this panel's own wording — a withholding is not an
          emptiness, and the sentence has to say so — while `unresolved` and
          `unknown` come from `gateSentence`, which is the point: a screen that
          wrote its own copy for those two would word them back into an
          accusation against somebody whose `/auth/me` had simply not landed.
        */}
        <GateNotice
          gate={directoryGate}
          what="read this finding’s escalations"
          onRetry={() => permissions.refetch()}
          refusedNode={
            <p className="rn-muted" data-escalation-history-refused>
              An escalation names people, so reading one is reading the workforce. You do not hold
              directory access, so this is withheld — not empty.
            </p>
          }
        />
        {!mayReadDirectory ? null : history.isLoading ? (
          <p className="rn-muted" role="status">
            Loading this finding’s escalations…
          </p>
        ) : history.isError ? (
          <LoadFailure
            error={history.error}
            message="We could not load this finding’s escalations."
            onRetry={() => void history.refetch()}
            refusedHeading="You cannot see this finding’s escalations"
          />
        ) : escalations.length === 0 ? (
          <p className="rn-muted" data-escalation-history-empty>
            This finding has never been escalated.
          </p>
        ) : (
          <ul className="rn-list">
            {escalations.map((record) => {
              const options = decidableCandidates(record.candidates);
              return (
                <li key={record.id} className="rn-col" data-escalation-record={record.outcome}>
                  <Answer answer={record} what="record" />

                  {/* The human decision that closes an ambiguity. Offered only
                      for an ambiguity that is still open, and only over the
                      candidates the ladder actually resolved. */}
                  {record.outcome === 'ambiguous' && record.resolvedAt === null && mayEscalate ? (
                    <div className="rn-col" data-escalation-decide={record.id}>
                      <h4 className="rn-card-title">Choose one of them</h4>
                      <p className="rn-muted">
                        You may only choose somebody your organization’s records already named for
                        this finding. Naming anybody else would be inventing authority, and the
                        server refuses it.
                      </p>
                      <label>
                        Escalate to
                        <select
                          value={chosen[record.id] ?? ''}
                          onChange={(event) =>
                            setChosen((prior) => ({ ...prior, [record.id]: event.target.value }))
                          }
                        >
                          <option value="">Choose a person…</option>
                          {options.map((candidate) => (
                            <option key={candidate.ordinal} value={candidate.deliveredUserId}>
                              {personLabel(candidate.delivered).label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <Button
                        onClick={() =>
                          decide.mutate({
                            escalationId: record.id,
                            targetUserId: chosen[record.id]!,
                          })
                        }
                        disabled={!chosen[record.id] || decide.isPending}
                        variant="secondary"
                      >
                        Record this decision
                      </Button>
                      {decide.isError ? (
                        <LoadFailure
                          error={decide.error}
                          message="We could not record that decision. The escalation is still waiting."
                          refusedHeading="You cannot decide this escalation"
                        />
                      ) : null}
                    </div>
                  ) : null}

                  {record.humanDecided ? (
                    <p className="rn-muted" data-escalation-decided>
                      A person chose between the candidates; this was not resolved automatically.
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
